import { Link } from "@tanstack/react-router";
import { Mail } from "lucide-react";
import { AppLogo } from "./app-logo";

interface FooterLinkProps {
  to: string;
  children: React.ReactNode;
  external?: boolean;
}

const FooterLink = ({ to, children, external }: FooterLinkProps) => {
  if (external) {
    return (
      <a
        href={to}
        className="text-sm text-neutral-500 transition-colors hover:text-neutral-900"
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  }
  return (
    <Link
      to={to}
      className="text-sm text-neutral-500 transition-colors hover:text-neutral-900"
    >
      {children}
    </Link>
  );
};

const Footer = () => {
  return (
    <footer className="border-t border-neutral-100 bg-white" role="contentinfo">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-4">
          {/* Brand */}
          <div className="md:col-span-2 space-y-4">
            <Link to="/" aria-label="Arsoc home">
              <AppLogo />
            </Link>
            <p className="max-w-xs text-sm text-neutral-500">
              Arsoc builds custom software, provides information security services, and
              delivers networking and CCTV hardware — helping businesses run and stay
              protected.
            </p>
            <a
              href="mailto:admin@arsoc.co.zw"
              className="inline-flex items-center gap-2 text-sm text-neutral-500 transition-colors hover:text-neutral-900"
            >
              <Mail className="h-4 w-4" aria-hidden="true" />
              admin@arsoc.co.zw
            </a>
            <div className="inline-flex items-center gap-2 text-xs font-medium text-neutral-400">
              <img src="/rothkos-icon.png" alt="" aria-hidden="true" className="h-5 w-5 object-contain" />
              A Rothkos Group company
            </div>
          </div>

          {/* Company links */}
          <div className="space-y-3">
            <p className="text-sm font-semibold text-neutral-900">Company</p>
            <nav className="flex flex-col space-y-2" aria-label="Company links">
              <FooterLink to="/about">About</FooterLink>
              <FooterLink to="/contact">Contact</FooterLink>
            </nav>
          </div>

          {/* Services + Legal */}
          <div className="space-y-3">
            <p className="text-sm font-semibold text-neutral-900">Services</p>
            <nav className="flex flex-col space-y-2" aria-label="Product links">
              <FooterLink to="/products/alpha">Alpha</FooterLink>
              <FooterLink to="/products/estman">Estman</FooterLink>
              <a
                href="/#services"
                className="text-sm text-neutral-500 transition-colors hover:text-neutral-900"
              >
                Information Security
              </a>
              <a
                href="/#services"
                className="text-sm text-neutral-500 transition-colors hover:text-neutral-900"
              >
                Networking & CCTV
              </a>
            </nav>
            <p className="mt-4 text-sm font-semibold text-neutral-900">Legal</p>
            <nav className="flex flex-col space-y-2" aria-label="Legal links">
              <FooterLink to="/privacy-policy">Privacy Policy</FooterLink>
              <FooterLink to="/terms">Terms of Service</FooterLink>
              <FooterLink to="/data-deletion">Data Deletion</FooterLink>
            </nav>
          </div>
        </div>

        <div className="mt-10 border-t border-neutral-100 pt-8 space-y-2">
          <p className="text-xs text-neutral-400">
            Alpha is deployed by Arsoc for client organisations including Goldenclem.
          </p>
          <p className="text-sm text-neutral-500">
            150 Nelson Mandela Avenue, Harare, Zimbabwe
          </p>
          <p className="text-sm text-neutral-500">
            © 2022 Arsoc, a Rothkos Group company. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
